/**
 * Event envelope and versioned event registry (02-architecture.md, "Commands,
 * events, audit"; stored in `platform.outbox_event`).
 *
 * A module declares each event once with `defineEvent(type, version, payload)`
 * and registers it; consumers parse an incoming envelope by `(type, version)`.
 * A payload change that is not purely additive gets a new version, and both
 * versions stay registered until no unconsumed events of the old one remain.
 *
 * Payload schemas describe the JSON form stored in `outbox_event.payload`
 * (jsonb), so money in a payload is a safe-integer number on the wire;
 * `parse` returns the decoded domain values (e.g. `Paise` bigint).
 *
 * No concrete domain events live here yet; modules add them as they are built.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { uuidSchema, type Uuid } from './ids.js';

export const ACTOR_KINDS = ['staff', 'customer', 'system', 'connector', 'api_client'] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];
export const actorKindSchema = z.enum(ACTOR_KINDS);

/**
 * Who caused a change, as recorded on events and audit rows.
 *
 * - `id` is the staff user, customer, connector install or API client id. It
 *   is null only for `system` (schedulers, the outbox relay), matching the
 *   nullable `audit_log.actor_id`.
 * - `terminalId` is the registered device for staff calls.
 * - `onBehalfOf` is the manager who approved an override
 *   (`audit_log.on_behalf_of`).
 * - `roleIds` from `Ctx.actor` is deliberately NOT carried: roles are
 *   authorisation input at request time and change later; an event records a
 *   fact, and the permission that allowed it is already checked. Keeping it
 *   out also keeps every outbox row smaller.
 */
export const actorSchema = z
  .object({
    kind: actorKindSchema,
    id: uuidSchema.nullable(),
    terminalId: uuidSchema.optional(),
    onBehalfOf: uuidSchema.optional(),
  })
  .refine((a) => a.kind === 'system' || a.id !== null, {
    message: 'Only system actors may have a null id',
    path: ['id'],
  });

export type Actor = z.output<typeof actorSchema>;

/**
 * Dotted, lowercase snake_case segments: `order.settled`, `order.kot_sent`,
 * `item.availability_changed`. Past tense is a naming rule for reviewers; it
 * is not machine-checked (irregular verbs like `sent`, `paid` make that
 * unreliable).
 */
export const EVENT_TYPE_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
export const eventTypeSchema = z.string().max(100).regex(EVENT_TYPE_PATTERN);

/** `outbox_event.version` is a `smallint`. */
export const EVENT_VERSION_MAX = 32_767;
export const eventVersionSchema = z.number().int().min(1).max(EVENT_VERSION_MAX);

const AGGREGATE_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;

const envelopeShape = {
  id: uuidSchema,
  type: eventTypeSchema,
  version: eventVersionSchema,
  companyId: uuidSchema,
  /** Null for company-level events (e.g. a customer or role change). */
  outletId: uuidSchema.nullable(),
  aggregateType: z.string().max(100).regex(AGGREGATE_TYPE_PATTERN),
  aggregateId: uuidSchema,
  occurredAt: isoTimestampSchema,
  requestId: z.string().min(1).max(200),
  actor: actorSchema,
};

/** Any envelope with an unparsed payload. */
export const eventEnvelopeSchema = z.object({ ...envelopeShape, payload: z.unknown() });

export interface EventEnvelope<T extends string = string, V extends number = number, P = unknown> {
  id: Uuid;
  type: T;
  version: V;
  companyId: Uuid;
  outletId: Uuid | null;
  aggregateType: string;
  aggregateId: Uuid;
  occurredAt: string;
  requestId: string;
  actor: Actor;
  payload: P;
}

export interface EventDefinition<T extends string, V extends number, P extends z.ZodType> {
  readonly type: T;
  readonly version: V;
  readonly payload: P;
  /** Full envelope schema with `type` and `version` pinned to this definition. */
  readonly envelope: z.ZodType<EventEnvelope<T, V, z.output<P>>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- variance: any definition at all
export type AnyEventDefinition = EventDefinition<string, number, any>;

/** The parsed envelope type a definition (or a union of them) produces. */
export type EventOf<D extends AnyEventDefinition> =
  D extends EventDefinition<infer T, infer V, infer P> ? EventEnvelope<T, V, z.output<P>> : never;

export function defineEvent<const T extends string, const V extends number, P extends z.ZodType>(
  type: T,
  version: V,
  payload: P,
): EventDefinition<T, V, P> {
  if (!EVENT_TYPE_PATTERN.test(type)) {
    throw new TypeError(
      `Event type "${type}" must be dotted lowercase snake_case, e.g. order.settled`,
    );
  }
  if (!eventVersionSchema.safeParse(version).success) {
    throw new TypeError(
      `Event version must be an integer in 1..${EVENT_VERSION_MAX}, got ${version}`,
    );
  }
  const envelope = z.object({
    ...envelopeShape,
    type: z.literal(type),
    version: z.literal(version),
    payload,
  }) as unknown as z.ZodType<EventEnvelope<T, V, z.output<P>>>;
  return Object.freeze({ type, version, payload, envelope });
}

export type EventParseFailure = 'invalid_envelope' | 'unknown_event' | 'invalid_payload';

export class EventParseError extends Error {
  readonly reason: EventParseFailure;
  readonly issues: z.core.$ZodIssue[];

  constructor(reason: EventParseFailure, message: string, issues: z.core.$ZodIssue[] = []) {
    super(message);
    this.name = 'EventParseError';
    this.reason = reason;
    this.issues = issues;
  }
}

export type EventSafeParseResult<E> =
  { success: true; data: E } | { success: false; error: EventParseError };

const keyOf = (type: string, version: number): string => `${type}@${version}`;

/**
 * Immutable registry of event definitions. `register` returns a new registry
 * whose parse result type includes the added definition.
 */
export class EventRegistry<D extends AnyEventDefinition = never> {
  readonly #defs: ReadonlyMap<string, AnyEventDefinition>;

  private constructor(defs: ReadonlyMap<string, AnyEventDefinition>) {
    this.#defs = defs;
  }

  static empty(): EventRegistry {
    return new EventRegistry(new Map());
  }

  register<N extends AnyEventDefinition>(def: N): EventRegistry<D | N> {
    const key = keyOf(def.type, def.version);
    if (this.#defs.has(key)) throw new Error(`Event ${key} is already registered`);
    const next = new Map(this.#defs);
    next.set(key, def);
    return new EventRegistry<D | N>(next);
  }

  has(type: string, version: number): boolean {
    return this.#defs.has(keyOf(type, version));
  }

  /**
   * True when this exact definition object is registered. A different
   * definition that merely reuses a registered `(type, version)` (another
   * payload schema) is not: emitting it would write events consumers cannot
   * parse with the registered schema.
   */
  includes(def: AnyEventDefinition): boolean {
    return this.#defs.get(keyOf(def.type, def.version)) === def;
  }

  get(type: string, version: number): D | undefined {
    return this.#defs.get(keyOf(type, version)) as D | undefined;
  }

  /** Registered `(type, version)` pairs, sorted. */
  list(): { type: string; version: number }[] {
    return [...this.#defs.values()]
      .map((d) => ({ type: d.type, version: d.version }))
      .sort((a, b) => a.type.localeCompare(b.type) || a.version - b.version);
  }

  safeParse(input: unknown): EventSafeParseResult<EventOf<D>> {
    const base = eventEnvelopeSchema.safeParse(input);
    if (!base.success) {
      return {
        success: false,
        error: new EventParseError(
          'invalid_envelope',
          'Event envelope is invalid',
          base.error.issues,
        ),
      };
    }
    const { type, version } = base.data;
    const def = this.#defs.get(keyOf(type, version));
    if (!def) {
      return {
        success: false,
        error: new EventParseError(
          'unknown_event',
          `No schema registered for ${keyOf(type, version)}`,
        ),
      };
    }
    const full = def.envelope.safeParse(input);
    if (!full.success) {
      return {
        success: false,
        error: new EventParseError(
          'invalid_payload',
          `Payload does not match ${keyOf(type, version)}`,
          full.error.issues,
        ),
      };
    }
    return { success: true, data: full.data as EventOf<D> };
  }

  /** Parse or throw `EventParseError`. */
  parse(input: unknown): EventOf<D> {
    const result = this.safeParse(input);
    if (!result.success) throw result.error;
    return result.data;
  }
}

/** Build a registry from definitions in one call. */
export function createEventRegistry<const Ds extends readonly AnyEventDefinition[]>(
  ...defs: Ds
): EventRegistry<Ds[number]> {
  let registry: EventRegistry<AnyEventDefinition> = EventRegistry.empty();
  for (const def of defs) registry = registry.register(def);
  return registry as unknown as EventRegistry<Ds[number]>;
}

/**
 * A payload that cannot be stored as JSON, or does not match its schema.
 * Always a programming error in the emitting command (500), never user input.
 */
export class EventPayloadError extends Error {
  readonly issues: z.core.$ZodIssue[];

  constructor(message: string, options: { cause?: unknown; issues?: z.core.$ZodIssue[] } = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'EventPayloadError';
    this.issues = options.issues ?? [];
  }
}

/** A JSON value as `JSON.parse` returns it. */
export type JsonValue =
  null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

/**
 * Domain payload -> the JSON wire form stored in `outbox_event.payload` (jsonb).
 *
 * `z.encode(def.payload, payload)` validates the domain value and runs every
 * codec backwards (`Paise` bigint -> safe-integer number). The result is then
 * round-tripped through `JSON.stringify`/`JSON.parse` and decoded again, so a
 * payload schema that is not JSON-safe (a raw `z.bigint()`, a `z.date()`, a
 * one-way transform) fails here, at emit time, instead of producing an event
 * no consumer can parse. Throws `EventPayloadError`.
 */
export function encodeEventPayload<P extends z.ZodType>(
  def: EventDefinition<string, number, P>,
  payload: z.output<P>,
): JsonValue {
  const where = keyOf(def.type, def.version);
  let wire: unknown;
  try {
    wire = z.encode(def.payload, payload);
  } catch (error) {
    throw new EventPayloadError(`Payload does not match ${where}`, {
      cause: error,
      issues: error instanceof z.ZodError ? error.issues : [],
    });
  }
  let json: JsonValue;
  try {
    json = JSON.parse(JSON.stringify(wire)) as JsonValue;
  } catch (error) {
    throw new EventPayloadError(`Payload of ${where} is not JSON-serialisable`, { cause: error });
  }
  const back = def.payload.safeParse(json);
  if (!back.success) {
    throw new EventPayloadError(`Payload of ${where} does not survive a JSON round trip`, {
      issues: back.error.issues,
    });
  }
  return json;
}
